/**
 * Производственный календарь: что за день и как он отмечается.
 *
 * Выходные рисуются ВСЕГДА, а не когда сервер соблаговолил прислать тип
 * дня. Суббота — свойство самой даты, страница знает его без запросов, и
 * ставить показ выходных в зависимость от наличия данных значило бы
 * прятать то, что и так известно.
 *
 * Производственный календарь добавляет не выходные, а исключения из них:
 * праздник в понедельник, перенесённую рабочую субботу, сокращённый день.
 * Поэтому день недели — основание, `dayType` — уточнение поверх него, а не
 * наоборот.
 *
 * @format
 */

import {toLocalDate} from './helpers';
import type {DayType, IsoDate} from './types';

/** Что именно нерабочего в этом дне. `null` — обычный рабочий. */
export type DayOff = 'WEEKEND' | 'HOLIDAY';

/**
 * Подпись нерабочего дня.
 *
 * Отметка на графике у выходного и праздника одна: и то, и другое — «в
 * этот день не работают», а отдельный цвет для праздника полез бы в
 * палитру смыслов ради того, что смыслом не является. Различает их слово.
 */
export const DAY_OFF_LABEL: Record<DayOff, string> = {
  WEEKEND: 'Выходной',
  HOLIDAY: 'Праздник',
};

/**
 * Суббота или воскресенье по самой дате.
 *
 * Через `toLocalDate`, а не `new Date(iso)`: ISO-дата без времени читается
 * как полночь UTC, и восточнее Гринвича `getDay()` вернул бы предыдущий
 * день — пятница считалась бы выходным раз в неделю.
 */
export function isWeekendDate(date: IsoDate): boolean {
  const local = toLocalDate(date);
  if (!local) return false;
  const weekday = local.getDay();
  return weekday === 0 || weekday === 6;
}

/**
 * Нерабочий ли день, с учётом производственного календаря.
 *
 * `dayType` перебивает день недели в обе стороны: перенесённая рабочая
 * суббота (`WORKDAY`) перестаёт быть выходным, а праздничный понедельник
 * (`HOLIDAY`) им становится. Сокращённый день остаётся рабочим — в нём
 * работают, просто меньше, и прощать в нём простой было бы неверно.
 */
export function dayOff(date: IsoDate, dayType?: DayType | null): DayOff | null {
  if (dayType === 'HOLIDAY') return 'HOLIDAY';
  if (dayType === 'WEEKEND') return 'WEEKEND';
  if (dayType === 'WORKDAY' || dayType === 'SHORTENED') return null;
  return isWeekendDate(date) ? 'WEEKEND' : null;
}

/** Непрерывный отрезок нерабочих дней. */
export interface DayOffSpan {
  from: IsoDate;
  to: IsoDate;
}

/**
 * Склеивает подряд идущие нерабочие дни в отрезки.
 *
 * Диаграммам на непрерывной оси нужны именно отрезки: суббота и
 * воскресенье, нарисованные двумя отдельными полосами, дают между собой
 * волосяной шов, которого в календаре нет.
 */
export function dayOffSpans(days: {date: IsoDate; dayType?: DayType | null}[]): DayOffSpan[] {
  const spans: DayOffSpan[] = [];
  let openSpan: DayOffSpan | null = null;

  days.forEach((day, index) => {
    if (!dayOff(day.date, day.dayType)) {
      openSpan = null;
      return;
    }
    // Отрезок продолжается, только если предыдущий день списка тоже был
    // нерабочим: дыра в календаре разрывает полосу, а не сшивает её.
    if (openSpan && index > 0 && openSpan.to === days[index - 1].date) {
      openSpan.to = day.date;
      return;
    }
    openSpan = {from: day.date, to: day.date};
    spans.push(openSpan);
  });

  return spans;
}
